import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, Loader, Smartphone, Edit2, Save } from 'lucide-react';
import { upiAPI } from '../../services/api';

const UpiIdForm = ({ userType, onUpdate }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [originalUpiData, setOriginalUpiData] = useState(null);
  const [upiData, setUpiData] = useState({
    upi_id: ''
  });
  const [upiInfo, setUpiInfo] = useState(null);

  useEffect(() => {
    loadUpiId();
  }, []);

  const loadUpiId = async () => {
    try {
      setLoading(true);
      setError('');
      const response = userType === 'partner'
        ? await upiAPI.getPartner()
        : await upiAPI.getOrganization();

      if (response.data.success) {
        setUpiInfo(response.data.data);
        // Pre-fill form if UPI ID exists
        if (response.data.data.upi_id) {
          const initialData = {
            upi_id: response.data.data.upi_id || ''
          };
          setUpiData(initialData);
          setOriginalUpiData(initialData);
        }
      }
    } catch (err) {
      console.error('Failed to load UPI ID:', err);
      setError(err.response?.data?.error || 'Failed to load UPI ID');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    if (!isEditing) return; // Prevent changes when not in edit mode

    const { name, value } = e.target;
    setUpiData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
    setSuccess('');
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError('');
    setSuccess('');
  };

  const handleCancel = () => {
    // Restore original data
    if (originalUpiData) {
      setUpiData({ ...originalUpiData });
    } else {
      // If no original data, reset to empty or current UPI info
      setUpiData({
        upi_id: upiInfo?.upi_id || ''
      });
    }
    setIsEditing(false);
    setError('');
    setSuccess('');
  };

  const validateUPI = (upiId) => {
    const upiRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z]{3,}$/;
    return upiRegex.test(upiId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent submission if not in edit mode
    if (!isEditing) {
      return;
    }

    setError('');
    setSuccess('');
    setSaving(true);

    // Validation
    if (!upiData.upi_id.trim()) {
      setError('UPI ID is required');
      setSaving(false);
      return;
    }

    if (!validateUPI(upiData.upi_id)) {
      setError('Invalid UPI ID format. Must be in format: yourname@okhdfc');
      setSaving(false);
      return;
    }

    try {
      const response = userType === 'partner'
        ? await upiAPI.updatePartner(upiData)
        : await upiAPI.updateOrganization(upiData);

      if (response.data.success) {
        setSuccess(response.data.message || 'UPI ID updated successfully');
        setUpiInfo(response.data.data);
        // Update form data
        const updatedData = { upi_id: upiData.upi_id };
        setUpiData(updatedData);
        setOriginalUpiData(updatedData);
        setIsEditing(false); // Exit edit mode after successful save
        if (onUpdate) {
          onUpdate();
        }
        // Reload after a short delay
        setTimeout(() => {
          loadUpiId();
        }, 1000);
      }
    } catch (err) {
      console.error('Failed to update UPI ID:', err);
      setError(err.response?.data?.error || 'Failed to update UPI ID');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="h-6 w-6 text-primary-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Verification Status */}
      {upiInfo && upiInfo.upi_id && (
        <div className={`p-4 rounded-lg border ${
          upiInfo.upi_verified
            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
            : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800'
        }`}>
          <div className="flex items-center space-x-2">
            {upiInfo.upi_verified ? (
              <>
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                  UPI ID verified
                </span>
              </>
            ) : (
              <>
                <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
                <span className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  UPI ID pending verification
                </span>
              </>
            )}
          </div>
          {upiInfo.upi_verified_at && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              Verified on {new Date(upiInfo.upi_verified_at).toLocaleDateString()}
            </p>
          )}
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center space-x-2 text-red-600 dark:text-red-400">
            <XCircle className="h-5 w-5" />
            <span className="text-sm">{error}</span>
          </div>
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
          <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
            <CheckCircle className="h-5 w-5" />
            <span className="text-sm">{success}</span>
          </div>
        </div>
      )}

      {/* Form */}
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Edit Button - Show when not editing */}
        {!isEditing && (
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={handleEdit}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center space-x-2"
            >
              <Edit2 className="h-4 w-4" />
              <span>Edit UPI ID</span>
            </button>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            UPI ID <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="upi_id"
            value={upiData.upi_id}
            onChange={handleChange}
            disabled={!isEditing}
            className={`w-full px-3 py-2 border border-gray-300 dark:border-dark-border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-dark-bg-primary dark:text-dark-text-primary ${
              !isEditing ? 'bg-gray-50 dark:bg-dark-bg-secondary cursor-not-allowed opacity-75' : ''
            }`}
            placeholder="yourname@okhdfc"
            required={isEditing}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            For faster weekly payouts, provide your UPI ID. This mode of payment will be preferred over Bank account transfers
          </p>
        </div>

        {/* Action Buttons - Show when editing */}
        {isEditing && (
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-dark-border">
            <button
              type="button"
              onClick={handleCancel}
              disabled={saving}
              className="px-4 py-2 bg-gray-200 dark:bg-dark-bg-secondary text-gray-700 dark:text-dark-text-primary rounded-lg hover:bg-gray-300 dark:hover:bg-dark-bg-primary transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XCircle className="h-4 w-4" />
              <span>Cancel</span>
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        )}
      </form>

      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-xs text-blue-800 dark:text-blue-200">
          <strong>Note:</strong> Your UPI ID will be verified by an administrator before you can receive payouts.
          You will be notified once verification is complete.
        </p>
      </div>
    </div>
  );
};

export default UpiIdForm;
